import { z } from "zod";
import { platformRoleSchema } from "../platform/roles.js";
import { timestampSchema, uuidSchema } from "../common/primitives.js";
import { featureKeySchema } from "./features.js";
import { permissionSchema, SYSTEM_ROLE_KEYS, scopeTypeSchema } from "./permissions.js";
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .max(254)
    .pipe(z.email("Geçerli bir e-posta adresi girin"));
/** Parola biçim kuralı; sızmış/yaygın parola denetimi sunucuda ayrıca yapılır. */
export const PASSWORD_MIN_LENGTH = 10;
export const newPasswordSchema = z
    .string()
    .min(PASSWORD_MIN_LENGTH, `Parola en az ${PASSWORD_MIN_LENGTH} karakter olmalıdır`)
    .max(200, "Parola en fazla 200 karakter olabilir");
export const totpCodeSchema = z
    .string()
    .trim()
    .regex(/^\d{6}$/, "Doğrulama kodu 6 haneli olmalıdır");
/** 6 haneli TOTP kodu ya da kurtarma kodu (xxxxx-xxxxx). */
export const mfaCodeSchema = z
    .string()
    .trim()
    .regex(/^(\d{6}|[a-z0-9]{5}-[a-z0-9]{5})$/i, "Doğrulama kodu ya da kurtarma kodu girin");
export const tenantStatusSchema = z.enum(["TRIAL", "ACTIVE", "SUSPENDED", "READ_ONLY", "CLOSED"]);
/** Salt okunurluğun nedeni (Prompt 24): elle (platform kararı) ya da ödenmeyen fatura (gecikme kuralı). */
export const READ_ONLY_REASONS = ["MANUAL", "BILLING"];
export const readOnlyReasonSchema = z.enum(READ_ONLY_REASONS);
// ── Giriş ────────────────────────────────────────────────────────────────────
export const loginRequestSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, "Parola zorunludur").max(200),
    deviceLabel: z.string().trim().max(100).optional().describe("Oturum listesinde görünecek cihaz adı"),
});
export const tokenPairSchema = z.object({
    accessToken: z.string(),
    accessTokenExpiresAt: timestampSchema,
    /** Yalnızca mobil/ajan istemcilerde gövdede döner; web'de httpOnly çerezdir. */
    refreshToken: z.string().optional(),
});
export const loginResponseSchema = z.discriminatedUnion("status", [
    tokenPairSchema.extend({ status: z.literal("AUTHENTICATED") }),
    z.object({
        status: z.literal("MFA_REQUIRED"),
        mfaToken: z.string().describe("Kısa ömürlü; /auth/mfa/verify çağrısında gönderilir"),
    }),
]);
export const mfaVerifyRequestSchema = z.object({ mfaToken: z.string().min(10), code: mfaCodeSchema });
export const refreshRequestSchema = z.object({
    refreshToken: z.string().min(20).optional().describe("Mobil/ajan: gövdede. Web: httpOnly çerezden okunur."),
});
export const switchTenantRequestSchema = z.object({ tenantId: uuidSchema });
export const reauthenticateRequestSchema = z
    .object({ password: z.string().min(1).max(200).optional(), code: mfaCodeSchema.optional() })
    .refine((value) => value.password !== undefined || value.code !== undefined, "Parola ya da doğrulama kodu girin");
// ── Parola ───────────────────────────────────────────────────────────────────
export const forgotPasswordRequestSchema = z.object({ email: emailSchema });
export const resetPasswordRequestSchema = z.object({
    token: z.string().min(20).max(300),
    newPassword: newPasswordSchema,
});
export const changePasswordRequestSchema = z.object({
    currentPassword: z.string().min(1).max(200),
    newPassword: newPasswordSchema,
});
/** Hesap varlığını sızdırmamak için her durumda aynı yanıt döner. */
export const acknowledgedSchema = z.object({ ok: z.literal(true) });
// ── 2FA ──────────────────────────────────────────────────────────────────────
export const mfaSetupResponseSchema = z.object({
    secret: z.string().describe("Base32 sır; elle giriş için"),
    otpauthUrl: z.string().describe("QR kod içeriği"),
});
export const mfaEnableRequestSchema = z.object({ code: totpCodeSchema });
export const mfaEnableResponseSchema = z.object({
    recoveryCodes: z.array(z.string()).describe("YALNIZCA bir kez gösterilir; güvenli bir yerde saklanmalıdır"),
});
export const mfaDisableRequestSchema = z.object({
    password: z.string().min(1).max(200),
    code: mfaCodeSchema,
});
// ── Oturumlar ────────────────────────────────────────────────────────────────
export const sessionSchema = z.object({
    id: uuidSchema,
    current: z.boolean(),
    clientKind: z.string(),
    deviceLabel: z.string().nullable(),
    userAgent: z.string().nullable(),
    ip: z.string().nullable(),
    createdAt: timestampSchema,
    lastUsedAt: timestampSchema,
});
export const sessionListSchema = z.object({ items: z.array(sessionSchema) });
export const sessionIdParamsSchema = z.object({ id: uuidSchema });
export const revokeOtherSessionsResponseSchema = z.object({ revoked: z.number().int() });
// ── /me ──────────────────────────────────────────────────────────────────────
export const membershipSchema = z.object({
    tenantId: uuidSchema,
    tenantName: z.string(),
    tenantStatus: tenantStatusSchema,
});
export const roleAssignmentViewSchema = z.object({
    roleId: uuidSchema,
    roleName: z.string(),
    roleKey: z
        .enum(SYSTEM_ROLE_KEYS)
        .nullable()
        .describe("Sistem rolüyse anahtarı (arayüz rolün çalışma alanını buna göre sadeleştirir); özel rolde null"),
    scopeType: scopeTypeSchema,
    scopeId: uuidSchema.nullable(),
});
/** Destek oturumu (Prompt 24): kiracı ekranındaki uyarı bandı ve destek personelinin kendi bandı. */
export const supportSessionSchema = z.object({
    grantId: uuidSchema,
    tenant: z.object({ id: uuidSchema, name: z.string() }),
    staffName: z.string(),
    expiresAt: timestampSchema,
});
/**
 * Kabuk bantları için hesap bildirimleri (Prompt 24): etkin destek erişimleri, onay bekleyen destek isteği sayısı ve okunmamış
 * ÖNEMLİ duyuru. `/me` ile birlikte gelir — her sayfa yüklemesinde ayrı istek atılmaz.
 */
export const accountNoticesSchema = z.object({
    activeSupport: z.array(supportSessionSchema),
    pendingSupportRequests: z.number().int(),
    importantAnnouncement: z
        .object({ id: uuidSchema, title: z.string(), body: z.string(), publishedAt: timestampSchema })
        .nullable()
        .describe("Okunmamış en yeni önemli duyuru"),
    unreadAnnouncements: z.number().int(),
});
export const meResponseSchema = z.object({
    user: z.object({
        id: uuidSchema,
        email: z.string(),
        fullName: z.string(),
        phone: z.string().nullable(),
        locale: z.string(),
        mfaEnabled: z.boolean(),
        isPlatformAdmin: z.boolean(),
        /** Platform personeli rolü (Prompt 24); yoksa null. Web "Platform paneli" bağlantısını buna göre gösterir. */
        platformRole: platformRoleSchema.nullable(),
    }),
    memberships: z.array(membershipSchema),
    /** Etkin kiracı yoksa (henüz seçilmemiş ya da üyelik yok) null. */
    activeTenant: z
        .object({
        id: uuidSchema,
        name: z.string(),
        status: tenantStatusSchema,
        readOnlyReason: readOnlyReasonSchema
            .nullable()
            .describe("Salt okunursa nedeni; BILLING → arayüz abonelik sayfasına yönlendirir"),
        planCode: z.string(),
        mfaRequired: z.boolean(),
        onboardingCompleted: z.boolean(),
        /** Tek istasyonlu kiracıda arayüz sadeleşir (AGENTS.md §2). */
        stationCount: z.number().int(),
        companyCount: z.number().int(),
        permissions: z.array(permissionSchema).describe("Herhangi bir kapsamda sahip olunan izinler"),
        /** `null` → tüm istasyonlar; dizi → yalnızca bu istasyonlar. */
        stationScope: z.array(uuidSchema).nullable(),
        assignments: z.array(roleAssignmentViewSchema),
        features: z.array(featureKeySchema).describe("Kiracı düzeyinde açık modüller"),
    })
        .nullable(),
    /** Kiracı 2FA'yı zorunlu kılıyor ama kullanıcı henüz açmamışsa true: arayüz kuruluma yönlendirir. */
    mfaSetupRequired: z.boolean(),
    /** Bu oturum bir destek oturumuysa (platform personeli kiracıyı salt okuyor) — web bandı ve "çık" düğmesi. */
    supportSession: supportSessionSchema.nullable(),
    /** Etkin kiracının bildirimleri (destek oturumunda ve kiracısız oturumda null). */
    notices: accountNoticesSchema.nullable(),
});
export const updateProfileRequestSchema = z.object({
    fullName: z.string().trim().min(1).max(200).optional(),
    phone: z.string().trim().max(30).nullable().optional(),
    locale: z.enum(["tr", "en"]).optional(),
});
//# sourceMappingURL=auth.js.map